import {
  Button,
  Divider,
  Flex,
  Icon,
  Spinner,
  theme,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import {
  ArrowLeftIcon,
  ChartBarIcon,
  ViewOffIcon,
} from '@tonic-one/react-icons';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUtcOffsetStore } from 'src/models/useUtcOffsetModel';
import { DrillDownItem, TimeRangeInterface } from 'src/types/log';

import { useLogReducerContext } from '../context';
import { LogTimeUnit } from '../shared';
import { LogTypeEnum } from '@Types/log';
import { ECharts } from 'src/ECharts';
import { ECElementEvent } from 'echarts/core';

const Chart = (props: {
  isLogChartOpen: boolean;
  toggleLogChart: (isOpen: boolean) => void;
}) => {
  const { isLogChartOpen, toggleLogChart } = props;
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  const shortDateFormat = useMemo(() => t('common.momentShortDateFormat'), [t]);
  const shortMonthDateFormat = useMemo(
    () => t('common.momentMonthDateFormat'),
    [t]
  );

  const { state, getChartData, setTimeRange, queryLogs, getState } =
    useLogReducerContext();
  const { timeRange, isChartLoading, chartData, loadChart } = state;
  // the current interval status
  const [interval, setInterval] = useState('');

  const [drillDownStack, setDrillDown] = useState<DrillDownItem[]>([]);
  const { offset } = useUtcOffsetStore();

  const getInterval = useCallback(
    (timeRange: TimeRangeInterface): string => {
      let tempInterval = '';
      if (timeRange.predefined === LogTimeUnit.Custom) {
        const endDate = dayjs(timeRange.end).format(shortDateFormat);
        const startDate = dayjs(timeRange.start).format(shortDateFormat);
        const daysDiff = dayjs(endDate).diff(startDate, 'day');
        const monthsDiff = dayjs(endDate).diff(startDate, 'month', true);
        if (daysDiff + 1 >= 30) {
          if (monthsDiff <= 1) {
            tempInterval = '1d';
          } else {
            tempInterval = '1M';
          }
        } else {
          tempInterval = '1d';
        }
      } else {
        switch (timeRange.predefined) {
          case 'all':
            tempInterval = '1M';
            break;
          case '1w':
          case '1M':
            tempInterval = '1d';
            break;
          case '24h':
            tempInterval = '1h';
            break;
        }
      }

      return tempInterval;
    },
    [shortDateFormat]
  );

  useEffect(() => {
    const init = async () => {
      if (!isLogChartOpen) {
        return;
      }
      const tempInterval = getInterval(timeRange);
      // init drill down stack
      setDrillDown([
        {
          ...timeRange,
          interval: tempInterval,
        },
      ]);
      setInterval(tempInterval);
      getChartData(timeRange, tempInterval);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadChart, isLogChartOpen]);

  const { xData, yData } = useMemo(
    () => ({
      xData: chartData.flatMap((data) => data[0]) as string[],
      yData: chartData.flatMap((data) => data[1]) as number[],
    }),
    [chartData]
  );

  const handleSeriesClick = useCallback(
    (event: ECElementEvent) => {
      if (interval === '1h') {
        return;
      }
      let drillDownInterval = '';
      if (interval === '1M') {
        drillDownInterval = '1d';
      } else if (interval === '1d') {
        drillDownInterval = '1h';
      } else {
        return;
      }
      const format = interval === '1M' ? shortMonthDateFormat : shortDateFormat;
      const baseDay = dayjs(`${event.name}`, `${format}`);
      let startDay = baseDay.startOf('day');
      let endDay = baseDay.endOf('day');
      if (interval === '1M') {
        startDay = baseDay.startOf('month');
        endDay = baseDay.endOf('month');
      }
      const drillDwonTimeRange: TimeRangeInterface = {
        ...timeRange,
        start: startDay.utcOffset(offset, true).toISOString(),
        end: endDay.utcOffset(offset, true).toISOString(),
        predefined: LogTimeUnit.Custom,
      };
      setDrillDown([
        ...drillDownStack,
        { ...drillDwonTimeRange, interval: drillDownInterval },
      ]);
      setInterval(drillDownInterval);
      setTimeRange({
        ...timeRange,
        ...drillDwonTimeRange,
        predefined: LogTimeUnit.Custom,
      });
      getChartData(drillDwonTimeRange, drillDownInterval);
      queryLogs(getState());
    },
    [
      drillDownStack,
      getChartData,
      getState,
      interval,
      offset,
      queryLogs,
      setTimeRange,
      shortDateFormat,
      shortMonthDateFormat,
      timeRange,
    ]
  );

  const onDrillUp = useCallback(async () => {
    if (drillDownStack.length < 2) {
      return;
    }
    const newDrillDownStack = [...drillDownStack];
    newDrillDownStack.pop();
    const lastDrillDownItem: DrillDownItem = newDrillDownStack.slice(-1)[0];
    const { interval: drillDownInterval, ...drillDownTimeRange } =
      lastDrillDownItem;
    setDrillDown(newDrillDownStack);
    setInterval(drillDownInterval);
    setTimeRange({
      ...drillDownTimeRange,
    });
    getChartData(drillDownTimeRange, drillDownInterval);
    queryLogs(getState());
  }, [drillDownStack, getChartData, getState, queryLogs, setTimeRange]);

  return (
    <Flex>
      {isLogChartOpen && (
        <Flex
          height="270px"
          mt="3x"
          mx="4x"
          alignItems="center"
          flexDirection="column"
          flex="auto"
        >
          <Flex flexDirection="column" flex="auto" width="100%">
            <Flex width="100%" gap="2x">
              {drillDownStack.length > 1 && (
                <>
                  <Button variant="secondary" onClick={onDrillUp}>
                    <Icon as={ArrowLeftIcon} />
                  </Button>
                  <Divider orientation="vertical" />
                </>
              )}
              <Button
                variant="secondary"
                onClick={() => {
                  toggleLogChart(false);
                  setDrillDown([]); // view-off and reopen will trigger reload chart data, so we clear the drill down status
                }}
              >
                <Icon as={ViewOffIcon} />
              </Button>
            </Flex>
            {isChartLoading ? (
              <Spinner margin="auto" />
            ) : (
              <Flex flex="auto" width="100%">
                <ECharts
                  animation={false}
                  grid={{ left: 0, bottom: 0, containLabel: true }}
                  xAxis={{
                    data: xData,
                    axisTick: {
                      customValues: xData,
                    },
                    axisLabel: {
                      rotate: 0,
                      width: 80,
                      overflow: 'break',
                      fontSize: 10,
                      hideOverlap: true,
                      customValues: xData,
                    },
                  }}
                  yAxis={{
                    splitLine: {
                      lineStyle: {
                        color: (theme.colors as any)[
                          colorStyle.background.fourth
                        ],
                      },
                    },
                  }}
                  tooltip={{
                    position: 'top',
                  }}
                  series={{
                    type: 'bar',
                    color: '#578AEF',
                    data: yData,
                    barMaxWidth: 40,
                    cursor: interval === '1h' ? 'auto' : 'pointer',
                    tooltip: {
                      formatter: (params) =>
                        `${params.name}<br />${params.marker}${t('log.chart.logEntries')}<span style="float: right; margin-left: 20px"><b>${params.data}</b></span>`,
                    },
                  }}
                  addChartEventListeners={(chart) => {
                    chart.on('click', 'series.bar', handleSeriesClick);
                  }}
                  removeChartEventListeners={(chart) => {
                    chart.off('click', handleSeriesClick);
                  }}
                />
              </Flex>
            )}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export const LogChart = React.memo(Chart);

export const LogChartToggle = (props: {
  logType: LogTypeEnum;
  isOpen: boolean;
  onToggle: (isOpen: boolean) => void;
}) => {
  const { logType, isOpen, onToggle } = props;

  if (logType === LogTypeEnum.URLTRACKING) {
    return <></>;
  }

  if (!isOpen) {
    return (
      <Button
        variant="ghost"
        onClick={() => {
          onToggle(!isOpen);
        }}
      >
        <Icon as={ChartBarIcon} />
      </Button>
    );
  }

  return <></>;
};
